import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { TESTIDS } from '@/constants/testIds';

const SPICE_MACRO = 'https://images.pexels.com/photos/3040873/pexels-photo-3040873.jpeg';

const IMG_RED_CHILLI = '/images/red-chilli-powder.jpg';
const IMG_TURMERIC = '/images/turmeric-powder.jpg';
const IMG_CORIANDER = '/images/coriander-powder.jpg';
const IMG_GARAM = '/images/garam-masala.jpg';
const IMG_KANDA = '/images/kanda-masala.jpg';

const PRODUCTS_HERO = '/images/products-banner.jpg';

const products = [
  { nameEn: 'Red Chilli Powder', nameHi: 'लाल मिर्च पावडर', desc: 'Bold & vibrant heat for every dish', image: IMG_RED_CHILLI },
  { nameEn: 'Turmeric Powder', nameHi: 'हळद पावडर', desc: 'Golden pure turmeric from Sangli & Hingoli', image: IMG_TURMERIC },
  { nameEn: 'Coriander Powder', nameHi: 'धने पावडर', desc: 'Fresh & earthy aroma for curries', image: IMG_CORIANDER },
  { nameEn: 'Garam Masala', nameHi: 'गरम मसाला', desc: 'A warming blend of finest whole spices', image: IMG_GARAM },
  { nameEn: 'Kanda Lasun Masala', nameHi: 'कांदा लसूण मसाला', desc: 'Traditional onion-garlic spice blend', image: IMG_KANDA },
];

const filters = [
  { id: 'all', label: 'All', testId: TESTIDS.PRODUCT_FILTER_ALL },
  { id: 'powder', label: 'Powders', testId: TESTIDS.PRODUCT_FILTER_POWDER },
  { id: 'blended', label: 'Blended Masalas', testId: TESTIDS.PRODUCT_FILTER_BLENDED },
];

export default function ProductsPage() {
  return (
    <div data-testid={TESTIDS.PRODUCTS_PAGE}>
      {/* ===== PAGE HERO ===== */}
      <section className="relative min-h-[50vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img src={PRODUCTS_HERO} alt="Spices" className="w-full h-full object-cover" loading="eager" />
          <div className="absolute inset-0 bg-[#1A1A1A]/70" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-32 md:py-40 w-full text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <p className="font-cinzel text-xs tracking-[0.25em] uppercase text-[#C9922C] mb-3">Our Range</p>
            <h1 className="font-playfair text-white text-4xl sm:text-5xl lg:text-6xl font-bold mb-4">
              30+ Premium Masala Varieties
            </h1>
            <p className="font-lato text-[#F9F3E8]/70 text-base md:text-lg">
              Carefully crafted blends for professional kitchens
            </p>
          </motion.div>
        </div>
      </section>

      {/* ===== PRODUCT GRID ===== */}
      <section className="bg-[#F9F3E8] py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {products.map((product, i) => (
              <motion.div
                key={product.nameEn}
                data-testid={`${TESTIDS.PRODUCT_CARD}-products-${i}`}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className="gold-corners bg-white border border-[#C9922C]/15 hover:border-[#C9922C]/60 hover:shadow-xl transition-all duration-500 group p-1"
              >
                <div className="overflow-hidden rounded-sm mx-2 mt-2">
                  <img src={product.image} alt={product.nameEn} className="w-full h-auto object-cover rounded-sm group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                </div>
                <div className="p-4">
                  <p className="font-hindi text-[#C9922C] text-sm mb-0.5">{product.nameHi}</p>
                  <h4 className="font-playfair text-[#1B3A6B] text-lg font-semibold mb-1">{product.nameEn}</h4>
                  <p className="font-lato text-[#5C3D11] text-xs leading-relaxed mb-3">{product.desc}</p>
                  <div className="flex items-center justify-between">
                    <span className="font-cinzel text-[9px] tracking-wider uppercase text-[#5C3D11]/50">Available in Bulk</span>
                    <Link
                      to="/contact"
                      data-testid={`${TESTIDS.PRODUCT_ENQUIRE_BTN}-${i}`}
                      className="font-cinzel text-[10px] tracking-wider uppercase text-[#C9922C] hover:text-[#E8A020] transition-colors flex items-center gap-1"
                    >
                      Enquire
                    </Link>
                  </div>
                </div>
              </motion.div>
            ))}

            {/* +30 More Card */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.4 }}
              className="flex items-center justify-center bg-[#1B3A6B] p-8 text-center min-h-[350px]"
            >
              <div>
                <p className="font-playfair text-[#C9922C] text-5xl font-bold mb-3">30+</p>
                <p className="font-cinzel text-[#F9F3E8] text-xs tracking-wider uppercase mb-2">Products Available</p>
                <p className="font-lato text-[#F9F3E8]/50 text-sm mb-6">Including whole spices, powders & custom blends</p>
                <Link to="/contact" className="btn-outline-gold text-xs py-2.5 px-5">
                  Ask for Full Catalog
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ===== CUSTOM BLEND ENQUIRY ===== */}
      <section className="bg-[#1B3A6B] py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }} transition={{ duration: 0.6 }}
          >
            <h2 className="font-playfair text-[#F9F3E8] text-3xl md:text-4xl mb-4">
              Need a Custom Spice Blend for Your Kitchen?
            </h2>
            <p className="font-lato text-[#F9F3E8]/65 text-base mb-8">
              We create bespoke masala blends tailored to your menu and kitchen requirements.
            </p>
            <Link to="/contact" className="btn-gold">
              Talk to Our Team
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
